import React, { useEffect, useMemo, useState } from "react";
import Chart from "../../common/ApexChart";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useLocation, useNavigate } from "react-router-dom";
import allApi from "../../../../api/allApi";
import Loader from "../../common/Loader";
import NmsChartCard from "../../common/NmsChartCard";
import NmsTableSearch, { searchRows, sortRows } from "../../common/NmsTableSearch";
import { useLocalization } from "../../localization/LocalizationContext";
import { useNetwork } from "../../common/networkContext";
import {
  PORT_USAGE_COLORS,
  getApexTheme,
  hasChartSeries,
  useDocumentTheme,
} from "../../common/nmsChartTheme";

const DEVICE_FIELDS = [
  "serialNumber",
  "deviceName",
  "model",
  "hardwareVersion",
  "swVersion",
  "health",
];

function WiredDashboard() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const theme = useDocumentTheme();
  const networkId = selectedNetworkId?.id ?? 6001;
  const location = useLocation();
  const navigate = useNavigate();

  const [portLoading, setPortLoading] = useState(true);
  const [poeLoading, setPoeLoading] = useState(true);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [portData, setPortData] = useState({ success: [], failure: [] });
  const [poeSeries, setPoeSeries] = useState([]);
  const [trafficSeries, setTrafficSeries] = useState([]);
  const [devices, setDevices] = useState([]);
  const [trafficDuration, setTrafficDuration] = useState("8h");
  const [license, setLicense] = useState("");
  const [tableQuery, setTableQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  useEffect(() => {
    setLicense(localStorage.getItem("license") || "");
  }, []);

  useEffect(() => {
    fetchPortUsage(networkId);
    fetchPoeUsage(networkId);
    fetchDevices(networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  useEffect(() => {
    fetchTraffic(trafficDuration, networkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trafficDuration, networkId]);

  const fetchPortUsage = async (id) => {
    setPortLoading(true);
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/wired/dashboard/portUsage?networkId=${id}`
      );
      setPortData({
        success: Array.isArray(response?.success) ? response.success : [],
        failure: Array.isArray(response?.failure) ? response.failure : [],
      });
    } catch (error) {
      console.error("Error fetching ports", error);
      setPortData({ success: [], failure: [] });
    } finally {
      setPortLoading(false);
    }
  };

  const fetchPoeUsage = async (id) => {
    setPoeLoading(true);
    try {
      const api = new allApi();
      const poeData = await api.getRequest(
        `api/wired/dashboard/poeUsage?networkId=${id}`
      );
      if (Array.isArray(poeData?.success)) {
        setPoeSeries([
          {
            name: localizationStrings?.poePowerUsage || "PoE",
            data: poeData.success.map((item) => ({
              x: item.serialNo,
              y: item.poe,
              z: item.total_power,
            })),
          },
        ]);
      } else {
        setPoeSeries([]);
      }
    } catch (error) {
      console.error("Error fetching POE usage", error);
      setPoeSeries([]);
    } finally {
      setPoeLoading(false);
    }
  };

  const fetchTraffic = async (duration, id) => {
    setTrafficLoading(true);
    try {
      const api = new allApi();
      const trafficData = await api.getRequest(
        `api/wired/dashboard/traffic/${duration}?networkId=${id}`
      );

      if (!Array.isArray(trafficData) || trafficData.length === 0) {
        setTrafficSeries([]);
        return;
      }

      const deviceMap = new Map();
      const xLabelSet = new Set();
      const isAggregatedByDay = duration === "7d" || duration === "30d";

      trafficData.forEach((device) => {
        const { device_id, data } = device;
        const labelMap = new Map();
        if (Array.isArray(data)) {
          data.forEach((item) => {
            let timestamp = new Date(item.interval1).getTime();
            if (isAggregatedByDay) {
              const date = new Date(timestamp);
              date.setHours(0, 0, 0, 0);
              timestamp = date.getTime();
            }
            xLabelSet.add(timestamp);
            labelMap.set(timestamp, (item.bandwidth / 1024).toFixed(2));
          });
        }
        deviceMap.set(device_id, labelMap);
      });

      const sortedTimestamps = Array.from(xLabelSet).sort((a, b) => a - b);
      setTrafficSeries(
        Array.from(deviceMap.entries()).map(([deviceId, labelMap]) => ({
          name: `Device ${deviceId}`,
          data: sortedTimestamps.map((ts) => ({
            x: ts,
            y: Number(labelMap.get(ts) || 0),
          })),
        }))
      );
    } catch (error) {
      console.error("Error fetching traffic", error);
      setTrafficSeries([]);
    } finally {
      setTrafficLoading(false);
    }
  };

  const fetchDevices = async (id) => {
    setDevicesLoading(true);
    try {
      const api = new allApi();
      const devicesResponse = await api.getRequest(
        `api/wired/dashboard/v2/devices?networkId=${id}`
      );
      const deviceArray = Array.isArray(devicesResponse)
        ? devicesResponse
        : Array.isArray(devicesResponse?.data)
          ? devicesResponse.data
          : Array.isArray(devicesResponse?.response)
            ? devicesResponse.response
            : [];
      setDevices(deviceArray);
    } catch (error) {
      console.error("Error fetching device data:", error);
      setDevices([]);
    } finally {
      setDevicesLoading(false);
    }
  };

  const refreshDevices = async () => {
    setRefreshing(true);
    await fetchDevices(networkId);
    setRefreshing(false);
  };

  const healthFilter = new URLSearchParams(location.search).get("health");
  const visibleDevices = useMemo(() => {
    const healthMatched = healthFilter
      ? devices.filter(
          (device) =>
            String(device?.health ?? "").toLowerCase() ===
            healthFilter.toLowerCase()
        )
      : devices;
    const searched = searchRows(healthMatched, tableQuery, DEVICE_FIELDS);
    return sortRows(searched, sortConfig.key, sortConfig.direction);
  }, [devices, healthFilter, tableQuery, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const deviceHealthClass = (health) => {
    const value = String(health ?? "").toLowerCase();
    if (health === 0 || value === "offline" || value === "0") return "offline";
    if (value === "awaitingconnection" || value === "awaiting") {
      return "awaitingConnection";
    }
    return "online";
  };

  const deviceHealthLabel = (health) => {
    if (health === 0 || health === "0") return "Offline";
    return health || "N/A";
  };

  const poeOptions = {
    ...getApexTheme({
      type: "bar",
      xaxisType: "category",
      yTitle: localizationStrings?.powerUsage,
      yFormatter: (val) => (val == null ? "0" : Number(val).toFixed(2)),
      tooltipY: (val) => `PoE: ${Number(val || 0).toFixed(2)}`,
    }),
    plotOptions: {
      bar: { horizontal: false, columnWidth: "28%", borderRadius: 4 },
    },
  };

  const trafficOptions = getApexTheme({
    duration: trafficDuration,
    yTitle: "Traffic (MB)",
    yFormatter: (val) => (val == null ? "0.00" : Number(val).toFixed(2)),
    tooltipY: (val) => `${Number(val || 0).toFixed(2)} MB`,
  });

  const portTooltip = ({ payload }) => {
    if (!payload?.length) return null;
    const row = payload[0]?.payload;
    return (
      <div className="nms-chart-tooltip">
        <strong>{row?.serialNumber}</strong>
        {payload
          .filter((item) => item.value > 0)
          .map((item) => (
            <p key={item.dataKey} style={{ color: item.fill }}>
              {item.name}: {item.value}
            </p>
          ))}
      </div>
    );
  };

  return (
    <div className="nms-dash">
      <div className="nms-dash-toolbar">
        <button
          className="text-btn primary-btn"
          onClick={() => navigate("/wired-config")}
        >
          {localizationStrings?.wiredConfiguration}
        </button>
      </div>

      <NmsChartCard
        title={localizationStrings?.usage}
        onExpand={() => navigate("/wiredusage-expansion")}
        localizationStrings={localizationStrings}
        loading={portLoading}
        hasData={portData.success.length > 0}
      >
        <div className="nms-port-legend">
          <ul>
            <li>
              <span className="d1"></span>
              {localizationStrings?.connectedUsingPower}
            </li>
            <li>
              <span className="d2"></span>
              {localizationStrings?.connectedNotUsingPower}
            </li>
            <li>
              <span className="d3"></span>
              {localizationStrings?.disabled}
            </li>
            <li>
              <span className="d4"></span>
              {localizationStrings?.error}
            </li>
            <li>
              <span className="d5"></span>
              {localizationStrings?.free}
            </li>
          </ul>
        </div>
        {portData.success.map((port) => (
          <div key={port.serialNumber || port.device_id} className="nms-port-row">
            <div className="nms-port-meta">
              <span>{port.serialNumber}</span>
              <span>
                {port.numOfPorts} {localizationStrings?.port}
                {port.numOfPorts > 1 ? "s" : ""}
              </span>
            </div>
            <ResponsiveContainer width="100%" height={36}>
              <BarChart
                layout="vertical"
                data={[port]}
                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              >
                <XAxis type="number" domain={[0, port.numOfPorts]} hide />
                <YAxis type="category" hide />
                <Tooltip
                  cursor={{ fill: "transparent" }}
                  content={portTooltip}
                />
                <Bar
                  dataKey="numOfActivePortsUsingPower"
                  stackId="a"
                  fill={PORT_USAGE_COLORS.usingPower}
                  name="Connected Using Power"
                />
                <Bar
                  dataKey="numOfActivePortsNotUsingPower"
                  stackId="a"
                  fill={PORT_USAGE_COLORS.notUsingPower}
                  name="Connected Not Using Power"
                />
                <Bar
                  dataKey="numOfDisabledPorts"
                  stackId="a"
                  fill={PORT_USAGE_COLORS.disabled}
                  name="Disabled"
                />
                <Bar
                  dataKey="numOfErrorPorts"
                  stackId="a"
                  fill={PORT_USAGE_COLORS.error}
                  name="Error"
                />
                <Bar
                  dataKey="numOfFreePorts"
                  stackId="a"
                  fill={PORT_USAGE_COLORS.free}
                  name="Free"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ))}
      </NmsChartCard>

      {license !== "Standard" && (
        <div className="two-column">
          <NmsChartCard
            title={localizationStrings?.poePowerUsage}
            onExpand={() => navigate("/wiredpower-expansion")}
            localizationStrings={localizationStrings}
            loading={poeLoading}
            hasData={hasChartSeries(poeSeries)}
          >
            <Chart
              key={`poe-${theme}`}
              options={poeOptions}
              series={poeSeries}
              type="bar"
              height={260}
            />
          </NmsChartCard>
          <NmsChartCard
            title={localizationStrings?.wiredTraffic}
            onExpand={() => navigate("/wiredtraffic-expansion")}
            duration={trafficDuration}
            onDurationChange={setTrafficDuration}
            localizationStrings={localizationStrings}
            loading={trafficLoading}
            hasData={hasChartSeries(trafficSeries)}
          >
            <Chart
              key={`traffic-${theme}-${trafficDuration}`}
              options={trafficOptions}
              series={trafficSeries}
              type="line"
              height={260}
            />
          </NmsChartCard>
        </div>
      )}

      <div className="inc-card">
        <div className="inc-card-title">
          <h3>
            {localizationStrings?.devices} ({visibleDevices.length})
          </h3>
          <div className="inc-card-button">
            <NmsTableSearch
              value={tableQuery}
              onChange={setTableQuery}
              placeholder={localizationStrings?.search || "Search devices"}
            />
            <button
              className="icon-btn"
              title="Refresh"
              onClick={refreshDevices}
            >
              <i
                className={`fa ${refreshing ? "fa-spinner fa-spin" : "fa-refresh"}`}
                aria-hidden="true"
              ></i>
            </button>
          </div>
        </div>
        <div className="inc-card-body">
          {devicesLoading ? (
            <Loader />
          ) : (
            <div className="inc-card-table">
              <table className="m-head">
                <thead>
                  <tr>
                    {[
                      ["serialNumber", localizationStrings?.serialNumber],
                      ["deviceName", localizationStrings?.deviceName],
                      ["model", localizationStrings?.model],
                      ["hardwareVersion", localizationStrings?.hardwareVersion],
                      ["swVersion", localizationStrings?.swVersion],
                      ["health", localizationStrings?.status],
                    ].map(([field, label]) => (
                      <th
                        key={field}
                        className={`inc-th-sort${
                          sortConfig.key === field ? " is-active" : ""
                        }`}
                        onClick={() => handleSort(field)}
                      >
                        {label}
                        <i className="fa fa-sort" aria-hidden="true" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleDevices.length ? (
                    visibleDevices.map((device, index) => (
                      <tr key={device.serialNumber || index}>
                        <td>{device.serialNumber || "N/A"}</td>
                        <td>{device.deviceName || "N/A"}</td>
                        <td>{device.model || "N/A"}</td>
                        <td>{device.hardwareVersion || "N/A"}</td>
                        <td>{device.swVersion || "N/A"}</td>
                        <td>
                          <span
                            className={`d-status ${deviceHealthClass(
                              device?.health
                            )}`}
                          >
                            {deviceHealthLabel(device?.health)}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">
                        <div className="nms-empty">
                          {localizationStrings?.noData}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WiredDashboard;
